import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import { ProfileContext } from "../../../context/ProfileProvider";
import { spotActivityDefaultLocalRange } from "./orderFilterHelpers";
import AuthService from "../../../api/services/AuthService";
import { mapMeOrderToOpenUiRow } from "./spotMeActivityHelpers";
import {
  SIDE_OPTIONS,
  TYPE_OPTIONS,
  buildPairFilterOptions,
  spotPairsDropdownToApi,
  spotSideDropdownToApi,
  spotUiOrderTypeToApi,
} from "./spotOrderFilterConstants";
import { spotMeActivityItemsFromResponse } from "../../../api/services/spotMeActivityQuery";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";
// Demo merge disabled — API-only spot open orders
// import { mergeSpotProfileDemoRows, SPOT_OPEN_ORDERS_DEMO_ROWS } from "./spotProfileOrdersStatic";

const ORDER_DESKTOP_COL_COUNT = 9;

function DatePickerCell({ value, onChange, ariaLabel, inputRef, placeholder = "From" }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">
        {value || placeholder}
      </span>
      <input
        ref={inputRef}
        type="date"
        className="so-dateCell__native"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    </div>
  );
}

const SpotOrders = () => {
  const { pairs: pairsFromProfile, fetchPairs } = useContext(ProfileContext) || {};

  const initRange = spotActivityDefaultLocalRange();

  const [filterType, setFilterType] = useState("all");
  const [side, setSide] = useState("all");
  const [selectedPair, setSelectedPair] = useState("all");
  const [dateFrom, setDateFrom] = useState(initRange.from);
  const [dateTo, setDateTo] = useState(initRange.to);
  const [rows, setRows] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(rows);

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  const pairFilterOptions = useMemo(() => buildPairFilterOptions(pairsFromProfile), [pairsFromProfile]);

  const apiPairParam = useMemo(() => spotPairsDropdownToApi(selectedPair), [selectedPair]);

  useEffect(() => {
    if ((!pairsFromProfile || pairsFromProfile.length === 0) && fetchPairs) {
      void fetchPairs();
    }
  }, [pairsFromProfile, fetchPairs]);

  useEffect(() => {
    if (selectedPair === "all") return;
    const ok = pairFilterOptions.some((o) => o.value === selectedPair);
    if (!ok) setSelectedPair("all");
  }, [pairFilterOptions, selectedPair]);

  const loadOrders = useCallback(async () => {
    const res = await AuthService.spotMeOrdersOpen({
      page: 1,
      page_size: 100,
      pair: apiPairParam || undefined,
      side: spotSideDropdownToApi(side) || undefined,
      type: spotUiOrderTypeToApi(filterType) || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    });
    const items = spotMeActivityItemsFromResponse(res);
    const mapped = items.map(mapMeOrderToOpenUiRow);
    setRows(mapped);
    // setRows(mergeSpotProfileDemoRows(mapped, SPOT_OPEN_ORDERS_DEMO_ROWS));
  }, [apiPairParam, dateFrom, dateTo, filterType, side]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const cancelOpenRow = async (row) => {
    if (!row?.id || !row.cancellable) return;
    const res = await AuthService.cancelOrder(row.id);
    if (res?.success) void loadOrders();
  };

  const resetFilters = () => {
    const { from, to } = spotActivityDefaultLocalRange();
    setFilterType("all");
    setSide("all");
    setSelectedPair("all");
    setDateFrom(from);
    setDateTo(to);
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        /* fall through */
      }
    }
    el?.click();
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;
    return (
      <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter spot open orders">
        <div className="so-toolbar__left fullwidthdesktop">
          <CustomDropdown
            id={`${pref}so-dd-type`}
            label="Type"
            value={filterType}
            options={TYPE_OPTIONS}
            onChange={setFilterType}
            menuKey={`${pref}so-type`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
          <CustomDropdown
            id={`${pref}so-dd-side`}
            label="Side"
            value={side}
            options={SIDE_OPTIONS}
            onChange={setSide}
            menuKey={`${pref}so-side`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
          <CustomDropdown
            id={`${pref}so-dd-pair`}
            label="Pair"
            value={selectedPair}
            options={pairFilterOptions}
            onChange={setSelectedPair}
            menuKey={`${pref}so-pair`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
          <div className="so-dateBlock">
            <span className="so-dateBlock__label">Date</span>
            <div className="so-dateRow">
              <DatePickerCell value={dateFrom} onChange={setDateFrom} placeholder="From" ariaLabel="Start date (updated_at)" inputRef={fromRef} />
              <span className="so-dateArrow" aria-hidden="true">
                →
              </span>
              <DatePickerCell value={dateTo} onChange={setDateTo} placeholder="To" ariaLabel="End date (updated_at)" inputRef={toRef} />
              <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-spot-title">
            <h1 id="so-spot-title" className="so-page__title">
              Open Orders
            </h1>

            <OrdersSubTabs
              tabs={[
                { label: "Open Orders", to: "/user_profile/orders/spot_orders" },
                { label: "Order History", to: "/user_profile/orders/spot_order_history" },
                { label: "Trade History", to: "/user_profile/orders/spot_position_history" },
              ]}
            />

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap">
              <table className="so-table">
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">
                      Side
                    </th>
                    <th scope="col">Pairs</th>
                    <th scope="col">
                      Price
                    </th>
                    <th scope="col">
                      Fill Price
                    </th>
                    <th scope="col">
                      Order Quantity
                    </th>
                    <th scope="col">
                      Pending Order Amount
                    </th>
                    <th scope="col">
                      Filled Amount
                    </th>
                    <th scope="col">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
                        <div className="so-emptyState" role="status">
                          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.time}</td>
                        <td>
                          <span className={row.side === "Buy" ? "so-side--buy" : "so-side--sell"}>{row.side}</span>
                        </td>
                        <td>{row.pairs}</td>
                        <td className="so-num">{row.price}</td>
                        <td className="so-num">{row.fillPrice}</td>
                        <td className="so-num">{row.orderQty}</td>
                        <td className="so-num">{row.pending}</td>
                        <td className="so-num">{row.filled}</td>
                        <td>
                          <button
                            type="button"
                            className="so-cancelBtn"
                            disabled={!row.cancellable}
                            onClick={() => cancelOpenRow(row)}
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Open orders pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Spot Orders</h2>
          <OrdersSubTabs
            tabs={[
              { label: "Open Orders", to: "/user_profile/orders/spot_orders" },
              { label: "Order History", to: "/user_profile/orders/spot_order_history" },
              { label: "Trade History", to: "/user_profile/orders/spot_position_history" },
            ]}
          />
          <div className="so-mobile__toolbar">
            <SoMobileFilters modalId="mob-spot-orders-filters" toolbar={renderToolbar(true, "mob")} onReset={resetFilters} />
          </div>
          {rows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            pagedRows.map((row) => (
              <OrderMobileCardShell
                key={`m-${row.id}`}
                desktopColumnCount={ORDER_DESKTOP_COL_COUNT}
                popupTitle="Open order details"
                heroPrimary={row.pairs}
                heroSecondary={
                  (() => {
                    const parts = [row.side, row.orderTypeLabel].filter((x) => x && x !== "—");
                    const line = parts.join(" ");
                    if (!line) return null;
                    const tone =
                      row.side === "Buy"
                        ? "orders-mobCard__subtype--buy"
                        : row.side === "Sell"
                          ? "orders-mobCard__subtype--sell"
                          : "";
                    return <span className={`orders-mobCard__subtype ${tone}`.trim()}>{line}</span>;
                  })()
                }
              >
                <div className="so-mobileCard__row">
                  <span>Time</span>
                  <span>{row.time}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Price</span>
                  <span className="so-num">{row.price}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Fill Price</span>
                  <span className="so-num">{row.fillPrice}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Order Quantity</span>
                  <span className="so-num">{row.orderQty}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Pending Order Amount</span>
                  <span className="so-num">{row.pending}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Filled Amount</span>
                  <span className="so-num">{row.filled}</span>
                </div>
                <div className="so-mobileCard__actions so-mobileCard__actions--split">
                  <span className="so-mobileCard__statusLabel">Status</span>
                  <button type="button" className="so-cancelBtn" disabled={!row.cancellable} onClick={() => cancelOpenRow(row)}>
                    Cancel
                  </button>
                </div>
              </OrderMobileCardShell>
            ))
          )}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Open orders pages"
          />
        </div>
      </div>
    </div>
  );
};

export default SpotOrders;
